<template>
  <div class="comment-composer">
    <div class="d-flex align-items-center mb-2" style="min-height: 30px">
      <b-dropdown
        v-if="permissions.can_change_status"
        size="sm"
        variant="link"
        no-caret
        dropup
        boundary="window"
        class="mr-2 flex-shrink-0"
        :toggle-class="['composer-chip', { 'composer-chip-active': toStatus }]"
        :disabled="processing"
      >
        <template #button-content>
          {{ toStatus ? `Cambiar a: ${toStatusLabel}` : 'Sin cambio de estado' }}
        </template>
        <b-dropdown-item
          v-for="option in statusOptions"
          :key="option.value || 'none'"
          :active="toStatus === option.value"
          @click="toStatus = option.value"
        >
          {{ option.text }}
        </b-dropdown-item>
      </b-dropdown>

      <b-button
        v-if="permissions.can_attach && !attachment"
        size="sm"
        class="composer-chip flex-shrink-0"
        :disabled="processing"
        @click="attachmentsModalShow = true"
      >
        <font-awesome-icon :icon="['far', 'paperclip']" class="mr-1" />
        Adjuntar
      </b-button>

      <span v-else-if="attachment" class="composer-chip composer-chip-active d-inline-flex align-items-center" style="min-width: 0; max-width: 220px">
        <font-awesome-icon :icon="attachment.type === 'document' ? ['far', 'file-alt'] : ['far', 'image']" class="mr-1 flex-shrink-0" />
        <span class="text-clamp-1">{{ attachmentName }}</span>
        <b-button
          variant="link"
          size="sm"
          class="p-0 ml-2 flex-shrink-0"
          style="color: inherit"
          aria-label="Quitar archivo"
          :disabled="processing"
          @click="attachment = null"
        >
          <font-awesome-icon :icon="['far', 'times']" />
        </b-button>
      </span>

      <span
        v-if="errorMessage || validationMessage"
        class="ml-auto fs-15 text-right text-clamp-2 pr-2"
        :class="errorMessage ? 'text-danger' : 'text-info'"
        style="min-width: 0"
        :title="errorMessage || validationMessage"
        role="status"
      >
        <font-awesome-icon :icon="['far', errorMessage ? 'exclamation-triangle' : 'info-circle']" />
        {{ errorMessage || validationMessage }}
      </span>
    </div>

    <div
      class="d-flex align-items-end bg-white border pl-3 pr-2 py-2"
      :style="{
        borderRadius: '10px',
        borderColor: focused ? '#163D7D !important' : '#d6dce5',
        boxShadow: focused ? '0 0 0 3px rgba(22, 61, 125, 0.12)' : '0 1px 3px rgba(16, 24, 40, 0.06)'
      }"
    >
      <b-form-textarea
        ref="textarea"
        v-model="content"
        :rows="MIN_ROWS"
        no-resize
        placeholder="Escriba un comentario…"
        aria-label="Comentario"
        class="composer-textarea border-0 shadow-none bg-transparent p-0 pr-2 fs-14"
        :style="{ lineHeight: `${LINE_HEIGHT}px` }"
        :disabled="processing"
        @focus="focused = true"
        @blur="focused = false"
        @keydown.enter.exact.prevent="submit"
      />

      <b-button
        size="sm"
        class="d-flex align-items-center justify-content-center border-0 p-0 flex-shrink-0"
        :style="{
          width: '32px',
          height: '32px',
          borderRadius: '6px',
          background: canSubmit || processing ? '#163D7D' : 'transparent',
          color: canSubmit || processing ? '#ffffff' : '#a3acb8'
        }"
        aria-label="Enviar"
        :disabled="!canSubmit"
        @click="submit"
      >
        <b-spinner v-if="processing" small style="width: 16px; height: 16px" />
        <svg v-else viewBox="0 0 24 24" width="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M20 5v7a3 3 0 0 1-3 3H5" />
          <path d="M9 11l-4 4 4 4" />
        </svg>
      </b-button>
    </div>

    <!-- attachments modal -->
    <Modal v-if="attachmentsModalShow" contentWidth="500px" :required="true" @close="attachmentsModalShow = false">
      <template #header>
        <p class="fs-20 font-weight-bold ml-1 mb-0" style="line-height: 0.675rem; color: #163D7D;">
          Adjuntar archivo al comentario
        </p>
      </template>
      <template #body>
        <AttachmentsUploader
          :allowedFileTypes="allowedFileTypes"
          :maxNumberOfFiles="1"
          :maxFileSize="10485760"
          :useDashboard="true"
          @file-added="onFileAdded"
          @file-removed="attachment = null"
          @handle-done-button="attachmentsModalShow = false"
        />
      </template>
      <template #footer>
        <span></span>
      </template>
    </Modal>
  </div>
</template>

<script>
import Modal from '../../shared/Modal'
import AttachmentsUploader from '../../shared/AttachmentsUploader.vue'
import { statusOptions, allowedFileTypes } from '../../../sharedOptions/drawingRequestOptions'

const MIN_CONTENT_LENGTH = 7
const MIN_ROWS = 3
const MAX_ROWS = 10
const LINE_HEIGHT = 21
const PDF_REQUIRED_STATUSES = ['revision', 'entregado']

export default {
  name: 'DrawingRequestShowCommentForm',
  components: { Modal, AttachmentsUploader },
  props: {
    drawingRequestId: { type: Number, required: true },
    currentStatus: { type: String, required: true },
    permissions: { type: Object, default: () => ({}) }
  },
  data () {
    return {
      MIN_ROWS,
      LINE_HEIGHT,
      content: '',
      toStatus: null,
      attachment: null,
      attachmentsModalShow: false,
      allowedFileTypes: allowedFileTypes,
      processing: false,
      errorMessage: null,
      focused: false
    }
  },
  computed: {
    statusOptions () {
      const options = statusOptions.filter(option => option.value !== this.currentStatus)
      return [{ value: null, text: 'Sin cambio de estado' }, ...options]
    },
    toStatusLabel () {
      const option = statusOptions.find(option => option.value === this.toStatus)
      return option ? option.text : ''
    },
    attachmentName () {
      if (!this.attachment) return ''
      return this.attachment.type === 'document' ? this.attachment.name : this.attachment.caption
    },
    hasPdf () {
      return !!this.attachment &&
        this.attachment.type === 'document' &&
        /\.pdf$/i.test(this.attachment.name || '')
    },
    contentLength () {
      return this.content.trim().length
    },
    validationMessage () {
      if (this.contentLength > 0 && this.contentLength < MIN_CONTENT_LENGTH) {
        return `El comentario debe tener al menos ${MIN_CONTENT_LENGTH} caracteres`
      }
      if (PDF_REQUIRED_STATUSES.includes(this.toStatus) && !this.hasPdf) {
        return `Debe adjuntar el plano en PDF para poder cambiar el estado a ${this.toStatusLabel.toLowerCase()}`
      }
      return null
    },
    canSubmit () {
      return !this.processing &&
        this.contentLength >= MIN_CONTENT_LENGTH &&
        !this.validationMessage
    }
  },
  watch: {
    content () {
      this.$nextTick(this.resizeTextarea)
    }
  },
  mounted () {
    this.resizeTextarea()
  },
  methods: {
    resizeTextarea () {
      const el = this.$refs.textarea && this.$refs.textarea.$el
      if (!el) return

      const maxHeight = MAX_ROWS * LINE_HEIGHT
      el.style.height = 'auto'
      el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`
      el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden'
    },
    onFileAdded (form) {
      this.attachment = form
      this.attachmentsModalShow = false
    },
    submit () {
      if (!this.canSubmit) return
      this.processing = true
      this.errorMessage = null

      this.$http.post(`/api/production/drawing_requests/${this.drawingRequestId}/comments`, this.buildBody())
        .then((res) => {
          this.$emit('created', res.data)
          this.reset()
        })
        .catch((error) => {
          const data = (error.response && error.response.data) || {}
          this.errorMessage = data.error || data.message || 'No se pudo enviar el comentario'
        })
        .finally(() => {
          this.processing = false
        })
    },
    buildBody () {
      const body = { comment: { content: this.content.trim() } }
      if (this.toStatus && this.permissions.can_change_status) body.comment.to_status = this.toStatus

      if (this.attachment && this.permissions.can_attach) {
        body.attachment = this.attachment.type === 'image'
          ? { image: this.attachment.image, caption: this.attachment.caption }
          : { file: this.attachment.file, name: this.attachment.name }
      }
      return body
    },
    reset () {
      this.content = ''
      this.toStatus = null
      this.attachment = null
    }
  }
}
</script>

<style scoped>
.comment-composer ::v-deep .composer-chip {
  height: 30px;
  padding: 0 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f6f7f9;
  color: #5f6873;
  font-size: 14px;
  line-height: 28px;
  text-decoration: none;
  box-shadow: none;
}
.comment-composer ::v-deep .composer-chip-active {
  border-color: #b6c6e3;
  background: #e7edf7;
  color: #163D7D;
  font-weight: 700;
}
.composer-textarea {
  font-size: 16px;
}
.composer-textarea::placeholder {
  color: #5f6873;
  font-size: 16px;
}
.comment-composer ::v-deep .composer-textarea::-webkit-scrollbar {
  width: 6px;
}
.comment-composer ::v-deep .composer-textarea::-webkit-scrollbar-track {
  background: transparent;
}
.comment-composer ::v-deep .composer-textarea::-webkit-scrollbar-thumb {
  background: rgba(22, 61, 125, 0.45);
  border-radius: 3px;
}
.comment-composer ::v-deep .composer-textarea::-webkit-scrollbar-thumb:hover {
  background: #163D7D;
}
@supports (-moz-appearance: none) {
  .comment-composer ::v-deep .composer-textarea {
    scrollbar-width: thin;
    scrollbar-color: rgba(22, 61, 125, 0.45) transparent;
  }
}
</style>
